import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import {
  computeSplitAreas,
  formatArea,
  getElementArea,
  summarizeSplitAreas,
} from "../utils/percentSplits";

/**
 * Tree badge for a percentage split — the single view/edit entry point.
 * Hovering opens a popover with the per-value areas in m²; clicking opens the
 * editor modal (when onEdit is provided):
 * - mode "area":   the element's own split, computed from its total area.
 * - mode "parent": aggregated across its area-data children (fetched on hover).
 *
 * Shared by access methods and damage combinations; callers supply the
 * icon, wording and how to read the stored value off an element.
 */
const SplitBadge = ({
  element,
  mode,
  onEdit,
  icon,
  getElementValue,
  popoverTitle,
  emptyLabel,
  unassignedLabel,
  className = "",
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const isParent = mode === "parent";
  const ownAreas = isParent
    ? []
    : computeSplitAreas(getElementValue(element), getElementArea(element));

  const handleMouseEnter = async () => {
    setOpen(true);

    if (!isParent) {
      setSummary({
        entries: ownAreas.map(({ name, area }) => ({ name, area })),
        unassignedArea: ownAreas.length ? 0 : getElementArea(element),
      });
      return;
    }

    try {
      setLoading(true);
      const parentIds =
        Array.isArray(element.groupedRootIds) && element.groupedRootIds.length
          ? element.groupedRootIds
          : [element.id];
      const responses = await Promise.all(
        parentIds.map((pid) =>
          api.get(`/bridge-structure/parent/${pid}/children`, {
            skipGlobalLoading: true,
          }),
        ),
      );
      const childMap = new Map();
      responses.forEach((res) => {
        (res.data?.data || []).forEach((child) => {
          if (!childMap.has(child.id)) childMap.set(child.id, child);
        });
      });
      setSummary(
        summarizeSplitAreas(Array.from(childMap.values()), getElementValue),
      );
    } catch (err) {
      console.error(`Error loading ${popoverTitle} summary:`, err);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const isEmpty = !isParent && ownAreas.length === 0;

  return (
    <span
      className={`badge badge-methods ${isEmpty ? "badge-methods-empty" : ""} ${
        onEdit ? "badge-methods-clickable" : ""
      } ${className}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setOpen(false)}
      role={onEdit ? "button" : undefined}
      onClick={
        onEdit
          ? (e) => {
              e.stopPropagation();
              setOpen(false);
              onEdit(element);
            }
          : undefined
      }
    >
      <FontAwesomeIcon icon={icon} />
      {isParent ? (
        <span>m²</span>
      ) : (
        ownAreas.length > 0 && <span>{ownAreas.length}</span>
      )}

      {open && (
        <span className="methods-popover">
          <span className="methods-popover-title">{popoverTitle}</span>
          {loading ? (
            <span className="methods-popover-row">
              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
            </span>
          ) : !summary ||
            (summary.entries.length === 0 && summary.unassignedArea === 0) ? (
            <span className="methods-popover-row methods-popover-muted">
              {summary ? "No area data available" : "Failed to load"}
            </span>
          ) : summary.entries.length === 0 ? (
            <span className="methods-popover-row methods-popover-muted">
              {emptyLabel}
            </span>
          ) : (
            <>
              {summary.entries.map(({ name, area }) => (
                <span key={name} className="methods-popover-row">
                  <span>{name}</span>
                  <span>{formatArea(area)} m²</span>
                </span>
              ))}
              {summary.unassignedArea > 0 && (
                <span className="methods-popover-row methods-popover-muted">
                  <span>{unassignedLabel}</span>
                  <span>{formatArea(summary.unassignedArea)} m²</span>
                </span>
              )}
            </>
          )}
          {onEdit && <span className="methods-popover-hint">Click to edit</span>}
        </span>
      )}
    </span>
  );
};

export default SplitBadge;
